import { __awaiter } from "tslib";
import { useCallback, useEffect, useRef, useState, useTransition, } from "react";
import { useDebounceCallback, useEventCallback } from "usehooks-ts";
import { AttributeDataType, } from "@plansoft-configuration-renderer/configuration-api";
import { ConsoleLogger, LogLevels, } from "@plansoft-configuration-renderer/utils";
import { EditUpdateBehavior } from "../types/EditUpdateBehavior";
import { isValueInRange } from "./helpers/isValueInRange";
import { useCsAttribute } from "./useCsAttribute";
import { useCsAttributeApi } from "./useCsAttributeApi";
import { useLocalEditValue } from "./useLocalEditValue";
import { useNumberFormat } from "./useNumberFormat";
import { useNumberParser } from "./useNumberParser";
const logger = new ConsoleLogger("useControlledNumericEdit", LogLevels.off);
// Number.MAX_SAFE_INTEGER throws an error in NodeJS timers
const MAX_SAFE_INT32 = 2147483647;
/**
 * Debouncing and parsing for numeric input
 *
 * @category Hook
 * @category CsAttribute
 * @param debounceMs
 * @param numberFormatOptions Formatting options, use a stable object reference for performance reason (useMemo)
 * @param callback
 * @returns
 */
export function useControlledNumericEdit(options) {
    // Holy 🍝 code ...
    const { onBackendValueSet, updateBehavior, debounceDelay, numberFormatOptions, } = options;
    // server and local edit value and api
    const localEditValue = useLocalEditValue();
    const attributeApi = useCsAttributeApi();
    const { valueMin, valueMax, valueMinExclusive, valueMaxExclusive, valueStepWidth, dataType, values = [], } = useCsAttribute();
    const parseNumber = useNumberParser(numberFormatOptions);
    const numberFormat = useNumberFormat(numberFormatOptions);
    const createOption = useCallback((value) => {
        // if we add the unit to the label, this breaks selecting the value
        const label = numberFormat.format(value.value);
        return {
            id: value.id,
            value: value.value,
            unitSymbol: value.unitSymbol,
            label,
            isSelected: false,
        };
    }, [numberFormat]);
    // we use a ref to prevent update cascade by the server sending a new array every time
    const optionsRef = useRef([]);
    useEffect(() => {
        if (values) {
            optionsRef.current = values.map(createOption);
        }
    }, [values, createOption]);
    const [isFocused, setIsFocused] = useState(false);
    const [isValidNumber, setIsValidNumber] = useState(true);
    const [isWithinBounds, setIsWithinBounds] = useState(true);
    const [isMatchingDataType, setIsMatchingDataType] = useState(true);
    const [isTransitioning, startTransition] = useTransition();
    // this is the value used while debouncing
    const [displayValue, setDisplayValue] = useState(undefined);
    const [parsedValue, setParsedValue] = useState(NaN);
    const setValue = useEventCallback((newValue) => {
        logger.log("setValue", { newValue });
        // we need that because setValue is async, this affects flushing of the debounce queue
        attributeApi.setLocalValue(newValue);
        // set the value as local edit value and in the backend and call the callback
        attributeApi.setValue(newValue).then(onBackendValueSet);
    });
    // we always use this, but we can set the delay to a very high value
    // we flush the debounce queue on blur to submit any changes
    // this saves us from having to detect value change in value in onBlur
    const setValueDebounced = useDebounceCallback(setValue, updateBehavior === EditUpdateBehavior.OnChange
        ? debounceDelay
        : MAX_SAFE_INT32);
    // we cancel the debounced fn if the user proceeds to enter invalid input
    // otherwise it will propagated partially entered values to the backend (flush) onBlur
    const cancelDebounce = useEventCallback(() => {
        logger.debug("cancelDebounce");
        setValueDebounced.cancel();
    });
    const handleValueChange = useEventCallback((newValue) => {
        // set this immediately outside startTransaction to improve input performance
        setDisplayValue(newValue);
        startTransition(() => {
            // reset attribute to undefined if the user deletes the input
            if (newValue === "") {
                setParsedValue(NaN);
                setIsValidNumber(true);
                setIsWithinBounds(true);
                setValueDebounced(undefined);
                return;
            }
            const [isParsedValueValid, parsedValue] = parseNumber(newValue);
            // set local hooks values
            setParsedValue(parsedValue);
            // reset validation
            setIsValidNumber(false);
            setIsWithinBounds(false);
            setIsMatchingDataType(false);
            if (!isParsedValueValid) {
                return cancelDebounce();
            }
            setIsValidNumber(true);
            // check if the parsed value is
            const isParsedValueInRange = isValueInRange({
                valueMin,
                valueMax,
                valueMinExclusive,
                valueMaxExclusive,
                valueStepWidth,
                value: parsedValue,
            });
            if (!isParsedValueInRange) {
                return cancelDebounce();
            }
            // flag as within bounds
            setIsWithinBounds(true);
            if (dataType === AttributeDataType.Integer) {
                if (!Number.isInteger(parsedValue)) {
                    return cancelDebounce();
                }
            }
            setIsMatchingDataType(true);
            // trigger propagation to local state and server
            setValueDebounced(parsedValue);
        });
    });
    const handleReset = useEventCallback(() => __awaiter(this, void 0, void 0, function* () {
        yield attributeApi.resetAttribute();
    }));
    const handleChange = useEventCallback((event) => {
        handleValueChange(event.currentTarget.value);
    });
    const handleBlur = useEventCallback(() => {
        startTransition(() => {
            setValueDebounced.flush(); // immediately execute pending executions
            setIsFocused(false);
            setDisplayValue(undefined);
            setParsedValue(NaN);
        });
    });
    const handleFocus = useEventCallback(() => {
        setIsFocused(true);
    });
    // use the hook local value or format the value from the server
    const inputValue = displayValue !== null && displayValue !== void 0 ? displayValue : (localEditValue != null ? numberFormat.format(localEditValue) : "");
    if (isFocused) {
        logger.debug({
            inputValue,
            displayValue,
            parsedValue,
            isValidNumber,
            isWithinBounds,
            isTransitioning,
        });
    }
    const isValid = isValidNumber && isWithinBounds && isMatchingDataType;
    return {
        inputValue,
        isValid,
        isFocused,
        isValidNumber,
        isWithinBounds,
        isMatchingDataType,
        options: optionsRef.current,
        handleValueChange,
        handleReset,
        handleChange,
        handleBlur,
        handleFocus,
    };
}
//# sourceMappingURL=useControlledNumericEdit.js.map